import {
  Component,
  forwardRef,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { Observable, Observer, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import {
  SelectListOption,
  SelectListOptionWithValue,
  SelectListOptionWithSubtext,
  instanceOfSelectListOptionWithValue
} from '../select-list-option';
import { AgpFormUtil } from '@liberty-financial/agp-shared';

/**
 * @deprecated  Please use `spark-shared-form-typeahead-field` component instead
 */
@Component({
  selector: 'app-typeahead-field',
  templateUrl: './typeahead-field.component.html',
  styleUrls: ['./typeahead-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TypeaheadFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TypeaheadFieldComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None
})
export class TypeaheadFieldComponent<T>
  implements OnInit, OnChanges, ControlValueAccessor
{
  private forwardChange = (_: any) => {};
  forwardTouched = () => {};

  @Input() name: string = '';
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() maxLength: number = 500;
  @Input() hasErrors: boolean = false;
  @Input() isAsync = true;
  @Input() dataSource:
    | SelectListOption[]
    | SelectListOptionWithSubtext[]
    | SelectListOptionWithValue<T>[] = [];
  @Input() dataSource$: (
    q: string
  ) => Observable<
    | SelectListOption[]
    | SelectListOptionWithSubtext[]
    | SelectListOptionWithValue<T>[]
  > = (_) => of(this.dataSource);
  @Input() isDisabled: boolean = false;
  @Input() waitMs: number = 300;
  @Input() minLength: number = 0;
  @Input() isFirstItemActive: boolean = false;
  @Input() isRequired: boolean = false;
  @Input() showHighlight: boolean = false;

  searchInProgress: boolean = false;
  search: string = '';

  source$: Observable<
    | SelectListOption[]
    | SelectListOptionWithSubtext[]
    | SelectListOptionWithValue<T>[]
  > = new Observable((observer: Observer<string>) => {
    observer.next(this.search);
  }).pipe(
    tap((_) => (this.searchInProgress = true)),
    switchMap((query) => {
      return (typeof query === 'string' && query === '') || query === null
        ? of([])
        : this.dataSource$(query);
    }),
    tap((_) => {
      this.searchInProgress = false;
    })
  );

  control: UntypedFormControl = new UntypedFormControl();
  form: UntypedFormGroup = new UntypedFormGroup({ control: this.control });
  value: SelectListOptionWithValue<T> | string | null = null;

  get hasLabel(): boolean {
    return !!this.label.length;
  }

  ngOnInit(): void {
    this.control.valueChanges.subscribe((value) => {
      this.search = value;

      // SC 26/5 - Update the value if they've typed in something different
      // from the last selection. Calling some Angular methods like
      // formControl.disable() / enable(), triggers valueChange passing
      // a string value instead of the last selected <T>.
      if (
        !(
          instanceOfSelectListOptionWithValue<T>(this.value) &&
          this.value.label == value
        )
      ) {
        this.value = value;
      }

      this.forwardChange(this.value);
    });
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges.isRequired) {
      AgpFormUtil.Validators.updateRequiredIfNecessary(this.control, simpleChanges.isRequired.currentValue);
    }
  }

  writeValue(value: any): void {
    this.value = value;
    if (instanceOfSelectListOptionWithValue<T>(this.value)) {
      this.control.setValue(this.value.label, { emitEvent: false });
    } else {
      this.control.setValue(this.value, { emitEvent: false });
    }
  }

  registerOnChange(onChangeFunction: any): void {
    this.forwardChange = onChangeFunction;
  }

  registerOnTouched(onTouchFunction: () => void): void {
    this.forwardTouched = onTouchFunction;
  }

  validate(_: UntypedFormControl) {
    if (this.control.valid) {
      return null;
    }
    return {
      invalid: true
    };
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;

    if (isDisabled) this.form.disable();
    else this.form.enable();
  }

  onSelect(event: TypeaheadMatch): void {
    this.writeValue(event.item);
    this.forwardChange(this.value);
  }
}
